import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { TicketEasyService } from "../../utils/ServiceLocator";
import { TicketStartCommand } from "./commands/TicketStartCommand";
import { TicketsSearchRequest } from "./commands/TicketsSearchRequest";
import { UserWorklistRequest } from "./commands/UserWorklistRequest";
import { Ticket } from "./model/ticketEasy/Ticket";
import { getToken } from "../../utils/AdalConfig";
import { TicketModulePostCommand } from "./commands/TicketModulePostCommand";
import { TicketModule } from "./model/ticketEasy/ticketModule/TicketModule";
import { TicketTypeConfig } from "./model/ticketEasy/ticketTypeConfig/TicketTypeConfig";
import { TicketTypeConfigPostCommand } from "./model/ticketEasy/ticketTypeConfig/TicketTypeConfigPostCommand";
import { TicketTypeConfigSearchRequest } from "./commands/TicketTypeConfigSearchRequest";
import { JobTitleMappingCommand } from "./commands/jobTitleMapping/JobTitleMappingCommand";
import { JobTitleMap } from "./model/ticketEasy/jobTitleMapping/JobTitleMap";
import { ApproverCommand } from "./commands/approver/ApproverCommand";
import { TicketApprover } from "./model/ticketEasy/ticketApprover/TicketApprover";
import { Department } from "./model/ticketEasy/department/Department";
import { TicketApprovalCommand } from "./commands/approval/TicketApprovalCommand";
import { TicketRejectCommand } from "./commands/approval/TicketRejectCommand";
import { TicketHoldCommand } from "./commands/approval/TicketHoldCommand";
import { TicketResumeCommand } from "./commands/approval/TicketResumeCommand";
import { TicketCloseCommand } from "./commands/approval/TicketCloseCommand";
import { CommentPostCommand } from "./commands/CommentPostCommand";
import { Comment } from "./model/ticketEasy/Comment";

export interface DocumentUploadResponse {
    success: boolean;
    fileId?: string;
    fileName?: string;
    error?: string;
}

const baseQuery = fetchBaseQuery({
    baseUrl: TicketEasyService,
    prepareHeaders: async (headers) => {
        const token = await getToken();
        if (token) {
            headers.set('Authorization', 'Bearer ' + token);
        }
        headers.set('content-type', 'application/json');
        return headers;
    }
});

export const ticketEasyApi = createApi({
    reducerPath: 'ticketEasyApi',
    baseQuery,
    tagTypes: ['ticket', 'ticketSearch', 'ticketModuleList', 'ticketTypeConfig', 'ticketTypeConfigList', 'jobTitleMapping','approverList', 'comment'],
    endpoints: (builder) => ({
        ticketCreate: builder.mutation<Ticket, TicketStartCommand>({
            query: (body) => {
                return { url: 'CreateTicket', method: 'POST', body: body };
            },
            invalidatesTags: ['ticket']
        }),
        ticketUpdate: builder.mutation<Ticket, Ticket>({
            query: (body) => {
                return { url: 'ticketUpdate', method: 'PUT', body: body };
            },
        }),
        ticketDelete: builder.mutation<Ticket, Ticket>({
            query: (body) => {
                return { url: 'ticketDelete', method: 'DELETE', body: body };
            },
        }),
        ticketSearch: builder.query<Ticket[], TicketsSearchRequest>({
            query: (request: TicketsSearchRequest) => `TicketSearch?module=${request.module}&searchCriteria=${request.searchCriteria}&viewType=${request.viewType || 'all'}`,
            providesTags: ['ticketSearch']
        }),
        ticketModulePost: builder.mutation<void, TicketModulePostCommand>({
            query: (body) => {
                return { url: 'TicketModulePost', method: 'POST', body: body };
            },
        }),
        ticketModuleList: builder.query<TicketModule[], void>({
            query: () => 'TicketModuleList',
            providesTags: ['ticketModuleList']
        }),
        ticketTypeConfigPost: builder.mutation<void, TicketTypeConfigPostCommand>({
            query: (body) => {
                return { url: 'TicketTypeConfigPost', method: 'POST', body: body };
            },
            invalidatesTags: ['ticketTypeConfig']            
        }),
        ticketTypeConfig: builder.query<TicketTypeConfig, string>({
            query: (id) => `TicketTypeConfig?id=${id}`,
            providesTags: ['ticketTypeConfig']
        }),
        ticketTypeConfigSearch: builder.query<TicketTypeConfig[], TicketTypeConfigSearchRequest>({
            query: (request: TicketTypeConfigSearchRequest) => `TicketTypeConfigSearch?module=${request.module}&searchCriteria=${request.searchCriteria}`,
            providesTags: ['ticketTypeConfigList']
        }),
        ticketTypeConfigRemoveJobTitleMap: builder.mutation<void, { ticketTypeConfigId: string; jobTitleMapId: string }>({
            query: ({ ticketTypeConfigId, jobTitleMapId }) => {
                return { url: 'TicketTypeConfigRemoveJobTitleMap', method: 'DELETE', body: { ticketTypeConfigId, jobTitleMapId } };
            },
            invalidatesTags: ['ticketTypeConfig']
        }),
        jobTitleMappingPost: builder.mutation<void, JobTitleMappingCommand>({
            query: (body) => {
                return { url: 'JobTitleMappingPost', method: 'POST', body: body };
            },
            invalidatesTags: ['jobTitleMapping']
        }),
        jobTitleMappings: builder.query<JobTitleMap[], void>({
            query: () => 'JobTitleMaps',
            providesTags: ['jobTitleMapping']
        }),
        jobTitleMapDelete: builder.mutation<void, string>({
            query: (id) => {
                return { url: `JobTitleMapDelete/${id}`, method: 'DELETE' };
            },
            invalidatesTags: ['jobTitleMapping']
        }),
        ticketTypeConfigApproverPost: builder.mutation<void, ApproverCommand>({
            query: (body) => {
                return { url: 'TicketTypeConfig/Approver', method: 'POST', body: body };
            },
            invalidatesTags: ['ticketTypeConfig']
        }),
        ticketTypeConfigApproverDelete: builder.mutation<void, { approverId: string }>({
            query: (approverId) => {
                return { url: 'TicketTypeConfigRemoveApprover', method: 'DELETE', body: approverId };
            },
            invalidatesTags: ['ticketTypeConfig']
        }),
        approverCreate: builder.mutation<void, ApproverCommand>({
            query: (body) => {
                return { url: 'Approver', method: 'POST', body: body };
            },
            invalidatesTags: ['approverList']
        }),
        approverUpdate: builder.mutation<void, ApproverCommand>({
            query: (body) => {
                return { url: 'Approver', method: 'PUT', body: body };
            },
            invalidatesTags: ['approverList']
        }),
        approverDelete: builder.mutation<void, string>({
            query: (id) => {
                return { url: `Approver/${id}`, method: 'DELETE' };
            },
            invalidatesTags: ['approverList']
        }),
        approversByType: builder.query<TicketApprover[], string>({
            query: (approverType) => `Approver/ByType/${approverType}`,
            providesTags: ['approverList']
        }),
        departments: builder.query<Department[], void>({
            query: () => 'Departments',
        }),
        ticketApprove: builder.mutation<void, TicketApprovalCommand>({
            query: (body) => {
                return { url: 'api/approvals/approve', method: 'POST', body: body };
            },
            invalidatesTags: ['ticket', 'ticketSearch']
        }),
        ticketReopen: builder.mutation<void, TicketApprovalCommand>({
            query: (body) => {
                return { url: 'api/approvals/reopen', method: 'POST', body: body };
            },
            invalidatesTags: ['ticket', 'ticketSearch']
        }),
        ticketReject: builder.mutation<void, TicketRejectCommand>({
            query: (body) => {
                return { url: 'api/approvals/reject', method: 'POST', body: body };
            },
            invalidatesTags: ['ticket', 'ticketSearch']
        }),        ticketHold: builder.mutation<void, TicketHoldCommand>({
            query: (body) => {
                return { url: 'holdTicket', method: 'POST', body: body };
            },
            invalidatesTags: ['ticket', 'ticketSearch']
        }),        ticketResume: builder.mutation<void, TicketResumeCommand>({
            query: (body) => {
                return { url: 'resumeTicket', method: 'POST', body: body };
            },
            invalidatesTags: ['ticket', 'ticketSearch']
        }),
        ticketClose: builder.mutation<void, TicketCloseCommand>({
            query: (body) => {
                return { url: 'closeTicket', method: 'POST', body: body };
            },
            invalidatesTags: ['ticket', 'ticketSearch']
        }),        
        commentPost: builder.mutation<Comment, CommentPostCommand>({
            query: (body) => {
                return { url: 'Comments', method: 'POST', body: body };
            },
            invalidatesTags: ['ticket', 'comment', 'ticketSearch']
        }),
        commentDelete: builder.mutation<void, { commentId: string; ticketId: string }>({
            query: (body) => {
                return { url: 'Comments', method: 'DELETE', body: body };
            },
            invalidatesTags: ['ticket', 'comment', 'ticketSearch']
        }),
        commentUpdate: builder.mutation<Comment, Comment>({
            query: (body) => {
                return { url: 'Comments', method: 'PUT', body: body };
            },
            invalidatesTags: ['ticket', 'comment', 'ticketSearch']
        }),
        commentList: builder.query<Comment[], string>({
            query: (ticketId) => `GetComments?ticketId=${ticketId}`,
            providesTags: ['comment']
        }),
        userWorklistQuery: builder.query<Ticket[], UserWorklistRequest>({
            query: (request: UserWorklistRequest) => `TicketWorklist?module=${request.module || ''}&viewType=${request.viewType || ''}&userId=${request.userId || ''}&userEmail=${request.userEmail || ''}`,
            providesTags: ['ticketSearch']
        }),
    }),
});

export const { useTicketCreateMutation, useTicketUpdateMutation, useTicketDeleteMutation, useTicketSearchQuery, useTicketModulePostMutation, useTicketModuleListQuery,
    useTicketTypeConfigPostMutation, useTicketTypeConfigQuery, useTicketTypeConfigSearchQuery, useJobTitleMappingPostMutation, useTicketTypeConfigRemoveJobTitleMapMutation,
    useJobTitleMappingsQuery, useJobTitleMapDeleteMutation, useTicketTypeConfigApproverPostMutation, useTicketTypeConfigApproverDeleteMutation,    useApproverCreateMutation, useApproverUpdateMutation, useApproverDeleteMutation, useApproversByTypeQuery, useDepartmentsQuery,
    useTicketApproveMutation, useTicketReopenMutation, useTicketRejectMutation, useTicketHoldMutation, useTicketResumeMutation, useTicketCloseMutation, useCommentPostMutation, useCommentDeleteMutation, useCommentUpdateMutation,
    useCommentListQuery, useUserWorklistQueryQuery
 } = ticketEasyApi;